import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Skeleton } from "@/components/ui/skeleton";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TimelineCounterLane } from "@/components/timeline/TimelineCounterLane";
import { formatDateBR } from "@/utils/dateUtils";
import { usePlataformaCarga, type PeriodoPlataforma } from "@/hooks/usePlataformaCarga";

const JANELA_EM_DIAS: Record<PeriodoPlataforma, number> = {
  "90d": 90,
  "12m": 365,
  "24m": 730,
};

function formatMesCurto(ms: number) {
  return new Date(ms).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
}

/**
 * Seção "Carga da plataforma" do Dashboard interno: freelancers e projetos
 * ativos por dia, na PLATAFORMA INTEIRA (não uma agência). Consome
 * get_plataforma_carga_diaria (sql/161), variante global guardada de
 * get_agencia_carga_diaria (mesmo componente TimelineCounterLane usado na
 * timeline da agência, sql/150/AgenciaTimeline.tsx).
 */
export function PlataformaCargaSection() {
  const [periodo, setPeriodo] = useState<PeriodoPlataforma>("12m");
  const { data, isLoading, isError } = usePlataformaCarga(periodo);

  const janela = useMemo(() => {
    const dataFinalMs = Date.now();
    const dataInicialMs = dataFinalMs - JANELA_EM_DIAS[periodo] * 24 * 60 * 60 * 1000;
    return { fromMs: dataInicialMs, toMs: dataFinalMs };
  }, [periodo]);

  const pontosFreelancers = useMemo(
    () => (data ?? []).map((ponto) => ({ dia: ponto.dia, valor: ponto.freelancers_ativos })),
    [data],
  );
  const pontosProjetos = useMemo(
    () => (data ?? []).map((ponto) => ({ dia: ponto.dia, valor: ponto.projetos_ativos })),
    [data],
  );

  const semAtividade =
    pontosFreelancers.every((ponto) => ponto.valor === 0) &&
    pontosProjetos.every((ponto) => ponto.valor === 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Carga da plataforma</CardTitle>
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={periodo}
          onValueChange={(novoPeriodo) => {
            if (novoPeriodo) setPeriodo(novoPeriodo as PeriodoPlataforma);
          }}
        >
          <ToggleGroupItem value="90d" aria-label="Últimos 90 dias">
            90 dias
          </ToggleGroupItem>
          <ToggleGroupItem value="12m" aria-label="Últimos 12 meses">
            12 meses
          </ToggleGroupItem>
          <ToggleGroupItem value="24m" aria-label="Últimos 24 meses">
            24 meses
          </ToggleGroupItem>
        </ToggleGroup>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : isError ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            Não foi possível carregar a carga da plataforma.
          </p>
        ) : semAtividade ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            Sem atividade registrada neste período.
          </p>
        ) : (
          <TooltipProvider delayDuration={100}>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <span className="w-[104px] shrink-0" aria-hidden />
                <div className="flex flex-1 justify-between">
                  <span>{formatMesCurto(janela.fromMs)}</span>
                  <span>hoje</span>
                </div>
              </div>
              <TimelineCounterLane
                label="Freelancers"
                windowFromMs={janela.fromMs}
                windowToMs={janela.toMs}
                nowPercent={100}
                pontos={pontosFreelancers}
                unidadeLabel="freelancers ativos"
                formatarDia={formatDateBR}
              />
              <TimelineCounterLane
                label="Projetos"
                windowFromMs={janela.fromMs}
                windowToMs={janela.toMs}
                nowPercent={100}
                pontos={pontosProjetos}
                unidadeLabel="projetos ativos"
                formatarDia={formatDateBR}
              />
            </div>
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  );
}

export default PlataformaCargaSection;
